import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { Link, useNavigate } from "react-router-dom";
import { listLeagues, listOrganizations, listPlayers, listRosterMemberships, listSeasons, listTeamPlayers, listTeams, listUserAssignments, listUsers as listAppUsers } from "../graphql/queries";
import { createPlayer, createRosterMembership, createTeam, createTeamPlayer, createUserAssignment, deletePlayer, deleteRosterMembership, deleteTeam, deleteTeamPlayer, deleteUserAssignment, updatePlayer, updateRosterMembership, updateTeam } from "../graphql/mutations";
import { formatApiError } from "../lib/formatApiError";
import AppDialog from "../components/AppDialog";
import { buildAdminScope } from "../lib/adminScope";
import { adminGet } from "../lib/adminQueries";
import { buildCoachTeamIds, buildPlayerTeamIds, getTeamCoachUsernames, type RosterMembershipLike, type UserAssignmentLike } from "../lib/relationshipScope";
import type { ActiveRoleScope } from "../lib/roleContext";

interface Team {
  id: string;
  name: string;
  organizationId?: string | null;
  programId?: string | null;
  leagueId?: string | null;
  seasonId?: string | null;
  ageGroup?: string | null;
  coachUsernames?: string[] | null;
}

interface Organization {
  id: string;
  name: string;
}

interface League {
  id: string;
  name: string;
  organizationId: string;
}

interface Program {
  id: string;
  name: string;
  organizationId: string;
  seasonId?: string | null;
  type: string;
}

interface Season {
  id: string;
  name: string;
  year: number;
}

interface CoachOption {
  username: string;
  email: string;
}
interface AppUser {
  id: string;
  email?: string | null;
  roles?: string[] | null;
}

interface Player {
  id: string;
  name: string;
  teamId: string;
}

interface TeamPlayerLink {
  id: string;
  teamID: string;
  playerID: string;
}

interface RosterMembership extends RosterMembershipLike {
  seasonId?: string | null;
  organizationId?: string | null;
  programId?: string | null;
}

interface UserAssignment extends UserAssignmentLike {}

interface TeamsPageProps {
  userRoles: string[];
  activeRoleScope?: ActiveRoleScope | null;
}

export default function TeamsPage({ userRoles, activeRoleScope }: TeamsPageProps) {
  const navigate = useNavigate();
  const listTeamsWithSeason = /* GraphQL */ `
    query ListTeamsWithSeason($limit: Int, $nextToken: String) {
      listTeams(limit: $limit, nextToken: $nextToken) {
        items {
          id
          name
          organizationId
          programId
          leagueId
          seasonId
          ageGroup
          coachUsernames
        }
        nextToken
      }
    }
  `;
  const listProgramsQuery = /* GraphQL */ `
    query ListProgramsForTeamsPage($limit: Int, $nextToken: String) {
      listPrograms(limit: $limit, nextToken: $nextToken) {
        items {
          id
          name
          organizationId
          seasonId
          type
        }
        nextToken
      }
    }
  `;

  const [username, setUsername] = useState("");
  const [teams, setTeams] = useState<Team[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [teamPlayers, setTeamPlayers] = useState<TeamPlayerLink[]>([]);
  const [rosterMemberships, setRosterMemberships] = useState<RosterMembership[]>([]);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([]);
  const [coaches, setCoaches] = useState<CoachOption[]>([]);

  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamAgeGroup, setNewTeamAgeGroup] = useState("");
  const [newOrgId, setNewOrgId] = useState("");
  const [newProgramId, setNewProgramId] = useState("");
  const [newLeagueId, setNewLeagueId] = useState("");
  const [newSeasonId, setNewSeasonId] = useState("");

  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const [editingTeamName, setEditingTeamName] = useState("");
  const [editingTeamProgramId, setEditingTeamProgramId] = useState("");
  const [editingTeamSeasonId, setEditingTeamSeasonId] = useState("");

  const [pendingCoachByTeamId, setPendingCoachByTeamId] = useState<Record<string, string>>({});
  const [coachSearchByTeamId, setCoachSearchByTeamId] = useState<Record<string, string>>({});
  const [playerSearchByTeamId, setPlayerSearchByTeamId] = useState<Record<string, string>>({});
  const [filterText, setFilterText] = useState("");
  const [filterOrgId, setFilterOrgId] = useState("");
  const [filterProgramId, setFilterProgramId] = useState("");
  const [filterLeagueId, setFilterLeagueId] = useState("");
  const [filterSeasonId, setFilterSeasonId] = useState("");
  const [filterAgeGroup, setFilterAgeGroup] = useState("");
  const [sortKey, setSortKey] = useState<"name" | "organization" | "program" | "league" | "season">("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const adminScope = useMemo(
    () => buildAdminScope({ userRoles, username, organizations, leagues, programs, teams, players, teamPlayers, rosterMemberships }),
    [leagues, organizations, players, programs, rosterMemberships, teamPlayers, teams, userRoles, username]
  );
  const canManage = adminScope.isGlobalAdmin || adminScope.isScopedCommish;
  const isCoachOnly = userRoles.includes("Coach") && !userRoles.includes("Commish") && !userRoles.includes("SuperAdmin");

  const loadTeams = async (silent = false) => {
    try {
      if (!silent) {
        setErrorMessage("");
      }
      const result: any = await API.graphql(graphqlOperation(listTeamsWithSeason, { limit: 500 }));
      const items: Team[] = (result?.data?.listTeams?.items ?? []).filter(Boolean);
      setTeams(items);
    } catch (error) {
      if (!silent) {
        setErrorMessage(`Failed to load teams: ${formatApiError(error)}`);
      }
    }
  };

  const loadPlayers = async (silent = false) => {
    try {
      const [playersResult, teamPlayersResult, rosterMembershipResult, userAssignmentsResult] = await Promise.all([
        API.graphql(graphqlOperation(listPlayers, { limit: 1000 })),
        API.graphql(graphqlOperation(listTeamPlayers, { limit: 4000 })),
        API.graphql(graphqlOperation(listRosterMemberships, { limit: 5000 })),
        API.graphql(graphqlOperation(listUserAssignments, { limit: 5000 }))
      ]);
      setPlayers(((playersResult as any)?.data?.listPlayers?.items ?? []).filter(Boolean));
      setTeamPlayers(((teamPlayersResult as any)?.data?.listTeamPlayers?.items ?? []).filter(Boolean));
      setRosterMemberships(((rosterMembershipResult as any)?.data?.listRosterMemberships?.items ?? []).filter(Boolean));
      setUserAssignments(((userAssignmentsResult as any)?.data?.listUserAssignments?.items ?? []).filter(Boolean));
    } catch (error) {
      if (!silent) {
        setErrorMessage(`Failed to load players: ${formatApiError(error)}`);
      }
    }
  };

  const loadMeta = async (silent = false) => {
    try {
      const [orgResult, programResult, leagueResult, seasonResult] = await Promise.all([
        API.graphql(graphqlOperation(listOrganizations, { limit: 500 })),
        API.graphql(graphqlOperation(listProgramsQuery, { limit: 500 })),
        API.graphql(graphqlOperation(listLeagues, { limit: 500 })),
        API.graphql(graphqlOperation(listSeasons, { limit: 500 }))
      ]);

      const orgItems = (orgResult as any)?.data?.listOrganizations?.items?.filter(Boolean) ?? [];
      const programItems = (programResult as any)?.data?.listPrograms?.items?.filter(Boolean) ?? [];
      const leagueItems = (leagueResult as any)?.data?.listLeagues?.items?.filter(Boolean) ?? [];
      const seasonItems = (seasonResult as any)?.data?.listSeasons?.items?.filter(Boolean) ?? [];

      setOrganizations(orgItems);
      setPrograms(programItems);
      setLeagues(leagueItems);
      setSeasons(seasonItems);
      const coachMap = new Map<string, CoachOption>();

      try {
        const appUsersRes: any = await API.graphql(graphqlOperation(listAppUsers, { limit: 500 }));
        const appUsers: AppUser[] = (appUsersRes?.data?.listUsers?.items ?? []).filter(Boolean);
        for (const user of appUsers) {
          const roles = user.roles ?? [];
          if (roles.includes("Coach") || roles.includes("Commish") || roles.includes("SuperAdmin")) {
            coachMap.set(user.id, { username: user.id, email: user.email ?? user.id });
          }
        }
      } catch {}

      try {
        const usersRes: any = await adminGet("/listUsers", { limit: 60 });
        const rawUsers = usersRes?.Users ?? [];
        const withGroups = await Promise.all(
          rawUsers.map(async (user: any) => {
            try {
              const groupsResponse: any = await adminGet("/listGroupsForUser", { username: user.Username, limit: 30 });
              const groups = (groupsResponse?.Groups ?? []).map((group: { GroupName: string }) => group.GroupName);
              return { ...user, groups };
            } catch {
              return { ...user, groups: [] };
            }
          })
        );
        for (const user of withGroups) {
          const groups = user.groups ?? [];
          if (groups.includes("Coach") || groups.includes("Commish") || groups.includes("SuperAdmin")) {
            const email = (user.Attributes ?? []).find((attribute: any) => attribute.Name === "email")?.Value ?? "";
            coachMap.set(user.Username, { username: user.Username, email: email || user.Username });
          }
        }
      } catch {}

      setCoaches(Array.from(coachMap.values()).sort((a, b) => (a.email || a.username).localeCompare(b.email || b.username)));
    } catch (error) {
      if (!silent) {
        setErrorMessage(`Failed to load organizations/leagues/coaches: ${formatApiError(error)}`);
      }
    }
  };

  const visibleOrganizations = useMemo(
    () => organizations.filter((organization) => adminScope.isGlobalAdmin || adminScope.canManageOrganization(organization.id)),
    [adminScope, organizations]
  );
  const visiblePrograms = useMemo(
    () => programs.filter((program) => adminScope.isGlobalAdmin || adminScope.canManageProgram(program.organizationId)),
    [adminScope, programs]
  );
  const visibleLeagues = useMemo(
    () => leagues.filter((league) => adminScope.isGlobalAdmin || adminScope.canManageLeague(league.id, league.organizationId)),
    [adminScope, leagues]
  );

  const leaguesForSelectedOrg = useMemo(
    () => (newOrgId ? visibleLeagues.filter((league) => league.organizationId === newOrgId) : visibleLeagues),
    [newOrgId, visibleLeagues]
  );
  const programsForSelectedOrg = useMemo(
    () => (newOrgId ? visiblePrograms.filter((program) => program.organizationId === newOrgId) : visiblePrograms),
    [newOrgId, visiblePrograms]
  );

  const orgById = useMemo(
    () => organizations.reduce<Record<string, string>>((acc, org) => ({ ...acc, [org.id]: org.name }), {}),
    [organizations]
  );

  const leagueById = useMemo(
    () => leagues.reduce<Record<string, string>>((acc, league) => ({ ...acc, [league.id]: league.name }), {}),
    [leagues]
  );
  const programLabelById = useMemo(
    () =>
      programs.reduce<Record<string, string>>((acc, program) => {
        acc[program.id] = `${program.name} (${program.type})`;
        return acc;
      }, {}),
    [programs]
  );
  const seasonLabelById = useMemo(
    () =>
      seasons.reduce<Record<string, string>>((acc, season) => {
        acc[season.id] = `${season.name} ${season.year}`;
        return acc;
      }, {}),
    [seasons]
  );

  const coachLabelByUsername = useMemo(
    () => coaches.reduce<Record<string, string>>((acc, coach) => ({ ...acc, [coach.username]: coach.email || coach.username }), {}),
    [coaches]
  );

  const playerById = useMemo(
    () => players.reduce<Record<string, Player>>((acc, player) => ({ ...acc, [player.id]: player }), {}),
    [players]
  );

  const playerTeamIds = useMemo(
    () => buildPlayerTeamIds({ players, teamPlayers, rosterMemberships }),
    [players, rosterMemberships, teamPlayers]
  );

  const coachTeamIds = useMemo(
    () => (isCoachOnly ? buildCoachTeamIds({ username, teams, userAssignments }) : new Set<string>()),
    [isCoachOnly, teams, userAssignments, username]
  );

  const playersByTeamId = useMemo(() => {
    const byTeam: Record<string, Player[]> = {};
    for (const [playerId, teamIds] of Object.entries(playerTeamIds)) {
      const player = playerById[playerId];
      if (!player) {
        continue;
      }
      for (const membershipTeamId of teamIds) {
        if (!byTeam[membershipTeamId]) {
          byTeam[membershipTeamId] = [];
        }
        if (!byTeam[membershipTeamId].some((item) => item.id === player.id)) {
          byTeam[membershipTeamId].push(player);
        }
      }
    }
    for (const key of Object.keys(byTeam)) {
      byTeam[key].sort((a, b) => a.name.localeCompare(b.name));
    }
    return byTeam;
  }, [playerById, playerTeamIds]);

  const coachUsernamesByTeamId = useMemo(
    () =>
      teams.reduce<Record<string, string[]>>((acc, team) => {
        acc[team.id] = getTeamCoachUsernames({ team, userAssignments });
        return acc;
      }, {}),
    [teams, userAssignments]
  );

  const sortedTeams = useMemo(() => {
    const commishTeams = adminScope.isScopedCommish ? teams.filter((team) => adminScope.canManageTeam(team)) : teams;
    const visibleTeams = isCoachOnly ? commishTeams.filter((team) => coachTeamIds.has(team.id)) : commishTeams;
    const normalizedText = filterText.trim().toLowerCase();
    const filtered = visibleTeams.filter((team) => {
      const orgName = orgById[team.organizationId ?? ""] ?? "";
      const programName = programLabelById[team.programId ?? ""] ?? "";
      const leagueName = leagueById[team.leagueId ?? ""] ?? "";
      const seasonName = seasonLabelById[team.seasonId ?? ""] ?? "";
      const haystack = `${team.name} ${orgName} ${programName} ${leagueName} ${seasonName} ${team.ageGroup ?? ""}`.toLowerCase();
      if (normalizedText && !haystack.includes(normalizedText)) {
        return false;
      }
      if (filterOrgId && team.organizationId !== filterOrgId) {
        return false;
      }
      if (filterProgramId && team.programId !== filterProgramId) {
        return false;
      }
      if (filterLeagueId && team.leagueId !== filterLeagueId) {
        return false;
      }
      if (filterSeasonId && team.seasonId !== filterSeasonId) {
        return false;
      }
      if (filterAgeGroup.trim() && (team.ageGroup ?? "").toLowerCase() !== filterAgeGroup.trim().toLowerCase()) {
        return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      let compare = 0;
      if (sortKey === "organization") {
        compare = (orgById[a.organizationId ?? ""] ?? "").localeCompare(orgById[b.organizationId ?? ""] ?? "");
      } else if (sortKey === "program") {
        compare = (programLabelById[a.programId ?? ""] ?? "").localeCompare(programLabelById[b.programId ?? ""] ?? "");
      } else if (sortKey === "league") {
        compare = (leagueById[a.leagueId ?? ""] ?? "").localeCompare(leagueById[b.leagueId ?? ""] ?? "");
      } else if (sortKey === "season") {
        compare = (seasonLabelById[a.seasonId ?? ""] ?? "").localeCompare(seasonLabelById[b.seasonId ?? ""] ?? "");
      } else {
        compare = a.name.localeCompare(b.name);
      }
      return sortDirection === "asc" ? compare : -compare;
    });
  }, [adminScope, coachTeamIds, filterAgeGroup, filterLeagueId, filterOrgId, filterProgramId, filterSeasonId, filterText, isCoachOnly, teams, sortDirection, sortKey, leagueById, orgById, programLabelById, seasonLabelById]);

  const toggleSort = (key: "name" | "organization" | "program" | "league" | "season") => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  const handleAddTeam = async () => {
    if (!newTeamName.trim()) {
      return;
    }

    if (!newOrgId && !newLeagueId) {
      setErrorMessage("A team must be assigned to an organization, a league, or both.");
      return;
    }
    if (!adminScope.canCreateTeamInScope(newOrgId || null, newLeagueId || null)) {
      setErrorMessage("Commish can only create teams inside assigned organizations or leagues.");
      return;
    }

    try {
      setErrorMessage("");
      const input: Record<string, unknown> = {
        name: newTeamName.trim()
      };
      if (newOrgId) {
        input.organizationId = newOrgId;
      }
      if (newProgramId) {
        input.programId = newProgramId;
      }
      if (newLeagueId) {
        input.leagueId = newLeagueId;
      }
      if (newSeasonId) {
        input.seasonId = newSeasonId;
      }
      if (newTeamAgeGroup.trim()) {
        input.ageGroup = newTeamAgeGroup.trim().toUpperCase();
      }

      const result: any = await API.graphql(
        graphqlOperation(createTeam, {
          input
        })
      );

      if (!result?.data?.createTeam) {
        throw new Error(formatApiError(result));
      }

      setNewTeamName("");
      setNewTeamAgeGroup("");
      setNewOrgId("");
      setNewProgramId("");
      setNewLeagueId("");
      setNewSeasonId("");
      await loadTeams(true);
    } catch (error) {
      setErrorMessage(`Failed to create team: ${formatApiError(error)}`);
    }
  };

  const handleEditTeam = async () => {
    if (!editingTeamId || !editingTeamName.trim()) {
      return;
    }
    const editingTeam = teams.find((team) => team.id === editingTeamId);
    if (editingTeam && !adminScope.canManageTeam(editingTeam)) {
      setErrorMessage("Commish can only edit assigned teams.");
      return;
    }

    try {
      setErrorMessage("");
      const result: any = await API.graphql(
        graphqlOperation(updateTeam, {
          input: {
            id: editingTeamId,
            name: editingTeamName.trim(),
            programId: editingTeamProgramId || null,
            seasonId: editingTeamSeasonId || null
          }
        })
      );
      if (!result?.data?.updateTeam) {
        throw new Error(formatApiError(result));
      }
      setEditingTeamId(null);
      setEditingTeamName("");
      setEditingTeamProgramId("");
      setEditingTeamSeasonId("");
      await loadTeams(true);
    } catch (error) {
      setErrorMessage(`Failed to update team: ${formatApiError(error)}`);
    }
  };

  const handleDeleteTeam = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this team?")) {
      return;
    }
    const team = teams.find((item) => item.id === id);
    if (team && !adminScope.canManageTeam(team)) {
      setErrorMessage("Commish can only delete assigned teams.");
      return;
    }

    try {
      setErrorMessage("");
      const result: any = await API.graphql(graphqlOperation(deleteTeam, { input: { id } }));
      if (!result?.data?.deleteTeam) {
        throw new Error(formatApiError(result));
      }
      await Promise.all([loadTeams(true), loadPlayers(true)]);
    } catch (error) {
      setErrorMessage(`Failed to delete team: ${formatApiError(error)}`);
    }
  };

  const handleAddCoach = async (team: Team) => {
    const coachUsername = pendingCoachByTeamId[team.id] ?? "";
    if (!coachUsername) {
      return;
    }

    try {
      setErrorMessage("");
      const existing = team.coachUsernames ?? [];
      if (existing.includes(coachUsername)) {
        return;
      }
      const ops: Promise<any>[] = [
        API.graphql(
          graphqlOperation(updateTeam, {
            input: {
              id: team.id,
              coachUsernames: [...existing, coachUsername]
            }
          })
        ) as Promise<any>
      ];
      const existingAssignment = userAssignments.find(
        (assignment) => assignment.teamId === team.id && assignment.userId === coachUsername && (assignment.relationship ?? "").toLowerCase().includes("coach")
      );
      if (!existingAssignment) {
        ops.push(
          API.graphql(
            graphqlOperation(createUserAssignment, {
              input: {
                userId: coachUsername,
                teamId: team.id,
                organizationId: team.organizationId ?? null,
                leagueId: team.leagueId ?? null,
                programId: team.programId ?? null,
                relationship: "Coach"
              }
            })
          ) as Promise<any>
        );
      }
      const [result] = await Promise.all(ops);
      if (!(result as any)?.data?.updateTeam) {
        throw new Error(formatApiError(result));
      }
      setPendingCoachByTeamId((prev) => ({ ...prev, [team.id]: "" }));
      setCoachSearchByTeamId((prev) => ({ ...prev, [team.id]: "" }));
      await loadTeams(true);
    } catch (error) {
      setErrorMessage(`Failed to add coach: ${formatApiError(error)}`);
    }
  };

  const handleRemoveCoach = async (team: Team, coachUsername: string) => {
    try {
      setErrorMessage("");
      const existing = team.coachUsernames ?? [];
      const updated = existing.filter((name) => name !== coachUsername);
      const result: any = await API.graphql(
        graphqlOperation(updateTeam, {
          input: {
            id: team.id,
            coachUsernames: updated
          }
        })
      );

      if (!result?.data?.updateTeam) {
        throw new Error(formatApiError(result));
      }
      const teamAssignments = userAssignments.filter(
        (assignment) => assignment.teamId === team.id && assignment.userId === coachUsername && (assignment.relationship ?? "").toLowerCase().includes("coach")
      );
      await Promise.all(
        teamAssignments.map((assignment) =>
          API.graphql(graphqlOperation(deleteUserAssignment, { input: { id: assignment.id } }))
        )
      );
      await loadTeams(true);
      await loadPlayers(true);
    } catch (error) {
      setErrorMessage(`Failed to remove coach: ${formatApiError(error)}`);
    }
  };

  const handleCreatePlayerForTeam = async (teamId: string, playerNameRaw: string) => {
    const playerName = playerNameRaw.trim();
    if (!playerName) {
      return;
    }

    try {
      setErrorMessage("");
      const team = teams.find((item) => item.id === teamId);
      const result: any = await API.graphql(
        graphqlOperation(createPlayer, {
          input: {
            name: playerName,
            teamId
          }
        })
      );
      if (!result?.data?.createPlayer) {
        throw new Error(formatApiError(result));
      }
      const createdPlayer = result.data.createPlayer as Player;
      if (team?.seasonId) {
        await API.graphql(
          graphqlOperation(createRosterMembership, {
            input: {
              playerId: createdPlayer.id,
              teamId,
              seasonId: team.seasonId,
              organizationId: team.organizationId ?? null,
              programId: team.programId ?? null,
              relationship: "ROSTERED",
              isPrimary: true
            }
          })
        );
      }
      setPlayerSearchByTeamId((prev) => ({ ...prev, [teamId]: "" }));
      await loadPlayers(true);
    } catch (error) {
      setErrorMessage(`Failed to add player: ${formatApiError(error)}`);
    }
  };

  const handleLinkExistingPlayerToTeam = async (teamId: string, playerId: string) => {
    if (!playerId) {
      return;
    }

    try {
      setErrorMessage("");
      const player = players.find((item) => item.id === playerId);
      const team = teams.find((item) => item.id === teamId);
      if (!player) {
        return;
      }
      if ((playerTeamIds[playerId] ?? new Set<string>()).has(teamId)) {
        return;
      }
      const result: any = await API.graphql(
        graphqlOperation(createTeamPlayer, {
          input: {
            teamID: teamId,
            playerID: playerId
          }
        })
      );
      if (!result?.data?.createTeamPlayer) {
        throw new Error(formatApiError(result));
      }
      if (team?.seasonId && !rosterMemberships.some((membership) => membership.playerId === playerId && membership.teamId === teamId)) {
        await API.graphql(
          graphqlOperation(createRosterMembership, {
            input: {
              playerId,
              teamId,
              seasonId: team.seasonId,
              organizationId: team.organizationId ?? null,
              programId: team.programId ?? null,
              relationship: "ROSTERED",
              isPrimary: false
            }
          })
        );
      }
      setPlayerSearchByTeamId((prev) => ({ ...prev, [teamId]: "" }));
      await loadPlayers(true);
    } catch (error) {
      setErrorMessage(`Failed to add existing player to team: ${formatApiError(error)}`);
    }
  };

  const handleDeletePlayerFromTeam = async (teamId: string, player: Player) => {
    try {
      setErrorMessage("");
      const linkForTeam = teamPlayers.find((link) => link.teamID === teamId && link.playerID === player.id);
      const membershipForTeam = rosterMemberships.find((membership) => membership.playerId === player.id && membership.teamId === teamId);

      if (player.teamId !== teamId) {
        if (linkForTeam) {
          const result: any = await API.graphql(graphqlOperation(deleteTeamPlayer, { input: { id: linkForTeam.id } }));
          if (!result?.data?.deleteTeamPlayer) {
            throw new Error(formatApiError(result));
          }
        }
        if (membershipForTeam) {
          await API.graphql(graphqlOperation(deleteRosterMembership, { input: { id: membershipForTeam.id } }));
        }
        await loadPlayers(true);
        return;
      }

      const alternateLinks = teamPlayers.filter((link) => link.playerID === player.id && link.teamID !== teamId);
      const alternateMemberships = rosterMemberships.filter((membership) => membership.playerId === player.id && membership.teamId !== teamId);
      if (alternateLinks.length > 0) {
        const nextPrimaryTeamId = alternateLinks[0].teamID;
        const updateResult: any = await API.graphql(
          graphqlOperation(updatePlayer, {
            input: {
              id: player.id,
              teamId: nextPrimaryTeamId
            }
          })
        );
        if (!updateResult?.data?.updatePlayer) {
          throw new Error(formatApiError(updateResult));
        }
        await API.graphql(graphqlOperation(deleteTeamPlayer, { input: { id: alternateLinks[0].id } }));
        if (membershipForTeam) {
          await API.graphql(graphqlOperation(deleteRosterMembership, { input: { id: membershipForTeam.id } }));
        }
        const nextPrimaryMembership =
          alternateMemberships.find((membership) => membership.teamId === nextPrimaryTeamId) ??
          null;
        if (nextPrimaryMembership) {
          await API.graphql(
            graphqlOperation(updateRosterMembership, {
              input: {
                id: nextPrimaryMembership.id,
                isPrimary: true
              }
            })
          );
        }
      } else {
        if (membershipForTeam) {
          await API.graphql(graphqlOperation(deleteRosterMembership, { input: { id: membershipForTeam.id } }));
        }
        const deleteResult: any = await API.graphql(graphqlOperation(deletePlayer, { input: { id: player.id } }));
        if (!deleteResult?.data?.deletePlayer) {
          throw new Error(formatApiError(deleteResult));
        }
      }

      await loadPlayers(true);
    } catch (error) {
      setErrorMessage(`Failed to remove player from team: ${formatApiError(error)}`);
    }
  };

  useEffect(() => {
    const load = async () => {
      const authUser = await Auth.currentAuthenticatedUser();
      setUsername(authUser?.username ?? "");
      await Promise.all([loadMeta(), loadTeams(), loadPlayers()]);
    };
    load();
  }, []);

  useEffect(() => {
    if (!activeRoleScope) {
      return;
    }
    if (activeRoleScope.targetType === "team" && activeRoleScope.teamId) {
      navigate(`/admin/teams/${activeRoleScope.teamId}`, { replace: true });
      return;
    }
    setFilterOrgId(activeRoleScope.organizationId ?? "");
    setFilterLeagueId(activeRoleScope.leagueId ?? "");
  }, [activeRoleScope, navigate]);

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Manage Teams</h1>
      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}

      {(adminScope.isGlobalAdmin || adminScope.isScopedCommish) && (
        <div className="bubble-card p-4 mb-4 flex gap-2">
          <button onClick={() => setCreateOpen(true)} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            Add Team
          </button>
        </div>
      )}

      <div className="bubble-card p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2">
        <input
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          className="border px-3 py-2 rounded"
          placeholder="Filter team/org/program/league/age"
        />
        <select value={filterOrgId} onChange={(event) => setFilterOrgId(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">All organizations</option>
          {visibleOrganizations.map((organization) => (
            <option key={organization.id} value={organization.id}>{organization.name}</option>
          ))}
        </select>
        <select value={filterProgramId} onChange={(event) => setFilterProgramId(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">All programs</option>
          {visiblePrograms.map((program) => (
            <option key={program.id} value={program.id}>{program.name}</option>
          ))}
        </select>
        <select value={filterLeagueId} onChange={(event) => setFilterLeagueId(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">All leagues</option>
          {visibleLeagues.map((league) => (
            <option key={league.id} value={league.id}>{league.name}</option>
          ))}
        </select>
        <select value={filterSeasonId} onChange={(event) => setFilterSeasonId(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">All seasons</option>
          {seasons.map((season) => (
            <option key={season.id} value={season.id}>{season.name} {season.year}</option>
          ))}
        </select>
        <input
          value={filterAgeGroup}
          onChange={(event) => setFilterAgeGroup(event.target.value)}
          className="border px-3 py-2 rounded"
          placeholder="Age group (11U)"
        />
      </div>

      <div className="bubble-table-wrapper">
        <table className="bubble-table">
          <thead>
            <tr>
              <th><button className="table-sort" onClick={() => toggleSort("name")}>Team</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("organization")}>Organization</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("program")}>Program</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("league")}>League</button></th>
              <th><button className="table-sort" onClick={() => toggleSort("season")}>Season</button></th>
              <th>Age Group</th>
              <th>Coaches</th>
              <th>Players</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedTeams.length === 0 && (
              <tr>
                <td colSpan={9} className="text-gray-600">No teams found.</td>
              </tr>
            )}
            {sortedTeams.map((team) => (
              <tr key={team.id}>
                <td><Link to={`/admin/teams/${team.id}`}>{team.name}</Link></td>
                <td>{team.organizationId ? (orgById[team.organizationId] ?? "Unknown") : "None"}</td>
                <td>{team.programId ? (programLabelById[team.programId] ?? "Unknown program") : "None"}</td>
                <td>{team.leagueId ? (leagueById[team.leagueId] ?? "Unknown") : "None"}</td>
                <td>{team.seasonId ? (seasonLabelById[team.seasonId] ?? "Unknown season") : "None"}</td>
                <td>{team.ageGroup ?? "Not set"}</td>
                <td>{(coachUsernamesByTeamId[team.id] ?? []).length}</td>
                <td>{(playersByTeamId[team.id] ?? []).length}</td>
                <td>
                  <div className="flex gap-2">
                    <Link to={`/admin/teams/${team.id}`} className="bubble-pill">Open</Link>
                    {canManage && adminScope.canManageTeam(team) && (
                      <>
                        <button
                          onClick={() => {
                            setEditingTeamId(team.id);
                            setEditingTeamName(team.name);
                            setEditingTeamProgramId(team.programId ?? "");
                            setEditingTeamSeasonId(team.seasonId ?? "");
                            setEditOpen(true);
                          }}
                          className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTeam(team.id)}
                          className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create Team"
        footer={
          <>
            <button onClick={() => setCreateOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button
              onClick={async () => {
                await handleAddTeam();
                setCreateOpen(false);
              }}
              className="bg-blue-600 text-white px-3 py-2 rounded"
            >
              Save
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input
            type="text"
            placeholder="Team name"
            value={newTeamName}
            onChange={(event) => setNewTeamName(event.target.value)}
            className="border px-3 py-2 rounded"
          />
          <input
            type="text"
            placeholder="Age group (11U)"
            value={newTeamAgeGroup}
            onChange={(event) => setNewTeamAgeGroup(event.target.value)}
            className="border px-3 py-2 rounded"
          />
          <select value={newOrgId} onChange={(event) => setNewOrgId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No organization</option>
            {visibleOrganizations.map((organization) => (
              <option key={organization.id} value={organization.id}>{organization.name}</option>
            ))}
          </select>
          <select value={newProgramId} onChange={(event) => setNewProgramId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No program</option>
            {programsForSelectedOrg.map((program) => (
              <option key={program.id} value={program.id}>{program.name}</option>
            ))}
          </select>
          <select value={newLeagueId} onChange={(event) => setNewLeagueId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No league</option>
            {leaguesForSelectedOrg.map((league) => (
              <option key={league.id} value={league.id}>{league.name}</option>
            ))}
          </select>
          <select value={newSeasonId} onChange={(event) => setNewSeasonId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No season</option>
            {seasons.map((season) => (
              <option key={season.id} value={season.id}>{season.name} {season.year}</option>
            ))}
          </select>
        </div>
      </AppDialog>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Team"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button
              onClick={async () => {
                await handleEditTeam();
                setEditOpen(false);
              }}
              className="bg-blue-600 text-white px-3 py-2 rounded"
            >
              Save
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input
            value={editingTeamName}
            onChange={(event) => setEditingTeamName(event.target.value)}
            className="border px-3 py-2 rounded w-full"
            placeholder="Team name"
          />
          <select value={editingTeamProgramId} onChange={(event) => setEditingTeamProgramId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No program</option>
            {visiblePrograms.map((program) => (
              <option key={program.id} value={program.id}>{program.name}</option>
            ))}
          </select>
          <select value={editingTeamSeasonId} onChange={(event) => setEditingTeamSeasonId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No season</option>
            {seasons.map((season) => (
              <option key={season.id} value={season.id}>{season.name} {season.year}</option>
            ))}
          </select>
        </div>
      </AppDialog>
    </div>
  );
}
